import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import DrawConfirmationModal from '@/components/DrawConfirmationModal';

// App-wide date rule: MM/DD/YYYY.
const fmtMDY = (val) => {
  if (!val) return '';
  const d = new Date(String(val).length <= 10 ? `${val}T00:00:00` : val);
  if (isNaN(d)) return String(val);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * Fetches draws awaiting deposit confirmation once per login and surfaces them
 * as an unmissable blocking modal, one at a time. Each action removes that draw
 * from the queue; a "dismiss for now" escape hatch avoids trapping the user
 * when many draws are pending.
 */
export default function DrawConfirmationManager({ user }) {
  const [queue, setQueue] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await apiService.getPendingDrawConfirmations(user.id);
      setQueue(res.data?.pending || []);
      setDismissed(false);
    } catch (e) { /* non-fatal */ }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  if (dismissed || queue.length === 0) return null;
  const draw = queue[0];
  if (!draw) return null;

  const remove = (id) => setQueue((q) => q.filter((d) => d.id !== id));

  const handleConfirm = async ({ depositDate, itemPayments }) => {
    try {
      await apiService.confirmReimbursementDeposit(draw.id, { deposit_date: depositDate, item_payments: itemPayments });
      toast.success(`Draw recorded — interest accruing from ${fmtMDY(depositDate)}`);
      remove(draw.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record the draw');
    }
  };
  const handleSnooze = async () => {
    try {
      await apiService.snoozeDrawConfirmation(draw.id);
      toast("We'll remind you again later");
      remove(draw.id);
    } catch (e) {
      toast.error('Could not snooze');
    }
  };
  const handleDiscard = async () => {
    try {
      await apiService.discardDrawConfirmation(draw.id);
      toast('Pending draw discarded');
      remove(draw.id);
    } catch (e) {
      toast.error('Could not discard');
    }
  };

  return (
    <DrawConfirmationModal
      key={draw.id}
      draw={draw}
      index={0}
      total={queue.length}
      onConfirm={handleConfirm}
      onSnooze={handleSnooze}
      onDiscard={handleDiscard}
      onDismiss={() => setDismissed(true)}
    />
  );
}
